// Session management utilities for temporary bot creation
// Primary: httpOnly cookie (server-set) | Backup: localStorage

const SESSION_COOKIE_NAME = 'boty_session';
const SESSION_STORAGE_KEY = 'boty_session_id';
const BOT_ID_STORAGE_KEY = 'boty_current_bot_id';
const SESSION_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

/**
 * Generate a cryptographically secure session ID
 * Uses crypto.randomUUID() for security
 */
export function generateSessionId(): string {
  return crypto.randomUUID();
}

/**
 * Get session ID from cookie (primary storage)
 * Returns null if not found or in server context
 * Note: httpOnly cookies cannot be read client-side, so this reads non-httpOnly fallback
 */
export function getSessionFromCookie(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === SESSION_COOKIE_NAME && value) {
        return decodeURIComponent(value);
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Get session ID from localStorage (client-side backup)
 * Returns null if not found or in server context
 */
export function getSessionFromStorage(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    return localStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    // localStorage may be blocked (incognito, security settings, etc.)
    // This is expected in some contexts - return null gracefully
    return null;
  }
}

/**
 * Get session ID from any available source
 * Checks cookie first (primary), then localStorage (backup)
 * Returns null if not found in any source
 */
export function getSessionId(): string | null {
  // Try cookie first (primary storage mechanism)
  const cookieSession = getSessionFromCookie();
  if (cookieSession) return cookieSession;

  // Fall back to localStorage backup
  return getSessionFromStorage();
}

/**
 * Save session ID to localStorage (client-side backup)
 */
export function saveSessionToStorage(sessionId: string): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(SESSION_STORAGE_KEY, sessionId);
  } catch {
    // localStorage may be blocked - this is a backup mechanism
    // The httpOnly cookie is the primary storage, so this failure is acceptable
  }
}

/**
 * Clear session from localStorage and the boty_session cookie
 */
export function clearSessionFromStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // localStorage may be blocked - silently ignore as this is cleanup
  }

  // Also clear the boty_session cookie (set expired to remove it)
  // Without this, getSessionId() still finds the cookie after deploy,
  // causing sign-in to redirect back to the "bot created" deploy page
  try {
    document.cookie = `${SESSION_COOKIE_NAME}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  } catch {
    // Cookie access may be restricted - silently ignore
  }
}

/**
 * Get or create a session ID (client-side)
 * 1. Check localStorage first (backup storage)
 * 2. If not found, generate new ID
 * 3. Save to localStorage
 *
 * Note: The httpOnly cookie is set server-side via /api/session
 * This function handles the client-side backup only
 */
export function getOrCreateClientSession(): string {
  // Check localStorage backup first
  const existingSession = getSessionFromStorage();
  if (existingSession) {
    return existingSession;
  }

  // Generate new session
  const newSession = generateSessionId();
  saveSessionToStorage(newSession);

  return newSession;
}

/**
 * Get bot ID from localStorage (for session recovery)
 * Returns null if not found or in server context
 */
export function getBotIdFromStorage(): string | null {
  if (typeof window === 'undefined') return null;

  try {
    return localStorage.getItem(BOT_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * Save bot ID to localStorage (for session recovery)
 */
export function saveBotIdToStorage(botId: string): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem(BOT_ID_STORAGE_KEY, botId);
  } catch {
    // localStorage may be blocked - silently ignore
  }
}

/**
 * Clear bot ID from localStorage
 */
export function clearBotIdFromStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.removeItem(BOT_ID_STORAGE_KEY);
  } catch {
    // localStorage may be blocked - silently ignore
  }
}

// Export constants for server-side usage
export { SESSION_COOKIE_NAME, SESSION_STORAGE_KEY, BOT_ID_STORAGE_KEY, SESSION_MAX_AGE };
